import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Sparkles, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { sendQuoteMessage, finalizeQuote } from "@/lib/api";
import { AI_AVATAR_URL } from "@/lib/media";

const SALES_EMAIL = "hello@trawelldmc.com";

const QUOTE_BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "AI Quote", path: "/quote" },
];

const QUICK_PROMPTS = [
  "5N Bangkok incentive for 120 pax, 4★, early May",
  "3N Dubai offsite for 40 board members",
  "4N Singapore conference, 250 pax, 5★",
  "5N Vietnam dealer trip, 180 pax",
];

export default function QuoteChat() {
  const [params] = useSearchParams();
  const initialSeed = (() => {
    const d = params.get("dest");
    const n = params.get("nights");
    if (d || n) {
      return `Hi, I'd like a quote for a ${n || ""}N trip to ${d || ""} — can you help?`;
    }
    return "";
  })();

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Welcome. I'm TrawellAI — I'll build you a first quote in under 2 minutes. Tell me about your trip: destination, nights, pax, and travel month. Or pick a quick-start below.",
    },
  ]);
  const [input, setInput] = useState(initialSeed);
  const [sending, setSending] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoff, setHandoff] = useState({ name: "", email: "", phone: "", company: "" });
  const [routed, setRouted] = useState(false);
  const [routing, setRouting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    const userId = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { id: userId, role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const res = await sendQuoteMessage({ session_id: sessionId, message: text });
      setSessionId(res.session_id);
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: res.reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: "assistant",
          content:
            "I ran into a connection issue. Please retry in a moment or submit the Inquiry form and a human consultant will respond within 24 hours.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleRoute = async (e) => {
    e.preventDefault();
    if (!sessionId) {
      toast.error("Start the chat first so we have context to route.");
      return;
    }
    if (!handoff.name || !handoff.email) return;
    setRouting(true);
    try {
      await finalizeQuote({ session_id: sessionId, ...handoff });
      setRouted(true);
      toast.success(`Query routed to ${SALES_EMAIL}`);
    } catch (err) {
      toast.error("Routing failed. Please try again.");
    } finally {
      setRouting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20" data-testid="quote-page">
      <Seo
        path="/quote"
        title="AI First Quote — Indicative DMC Pricing in 2 Minutes"
        description="Talk to TrawellAI. Get an instant indicative first quote (per-pax + total) for your trip across Dubai, Singapore, Malaysia, Thailand, Hong Kong, Philippines, or Vietnam."
        breadcrumb={QUOTE_BREADCRUMB}
      />
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#FF5238] mb-8">
        <ArrowLeft className="h-4 w-4" /> Back to home
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4">
          <div className="sticky top-28">
            <div className="flex items-center gap-3 mb-6">
              <img src={AI_AVATAR_URL} alt="" className="h-12 w-12 rounded-full" />
              <div>
                <div className="font-heading text-xl font-semibold text-[#0B192C]">TrawellAI</div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online · responds in seconds
                </div>
              </div>
            </div>
            <div className="overline mb-3">What I can do</div>
            <ul className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <li className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-[#FF5238] mt-0.5 shrink-0" />
                Qualify your brief in 4–6 questions
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-[#FF5238] mt-0.5 shrink-0" />
                Return indicative per-pax & total pricing
              </li>
              <li className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-[#FF5238] mt-0.5 shrink-0" />
                Hand off to a human for a formal proposal
              </li>
            </ul>

            <div className="mt-10">
              <div className="overline mb-3">Quick-start</div>
              <div className="space-y-2">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleSend(p)}
                    className="w-full text-left text-sm text-[#0B192C] border border-slate-200 hover:border-[#FF5238] hover:text-[#FF5238] transition-colors px-4 py-3"
                    data-testid={`quick-prompt-${p.slice(0, 8).replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <div className="bg-white border border-slate-200 flex flex-col" style={{ minHeight: "70vh" }}>
            <div className="px-6 py-4 border-b border-slate-100 bg-[#FDFCF8] flex items-center justify-between">
              <div>
                <div className="font-heading text-base font-semibold text-[#0B192C]">AI First Quote</div>
                <div className="text-xs text-slate-500">Powered by Claude Sonnet 4.5 · Responses are indicative</div>
              </div>
              <div className="text-xs text-slate-500">Session: {sessionId ? sessionId.slice(0, 8) : "—"}</div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4" style={{ maxHeight: "60vh" }}>
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-3`}>
                  {m.role === "assistant" && (
                    <img src={AI_AVATAR_URL} alt="" className="h-8 w-8 rounded-full shrink-0" />
                  )}
                  <div
                    data-testid={`quote-msg-${m.role}`}
                    className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#0B192C] text-white"
                        : "bg-[#FDFCF8] border border-slate-200 text-[#0B192C]"
                    }`}
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start gap-3">
                  <img src={AI_AVATAR_URL} alt="" className="h-8 w-8 rounded-full" />
                  <div className="bg-[#FDFCF8] border border-slate-200 px-4 py-3 text-sm text-slate-400">
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce" />
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:120ms]" />
                      <span className="h-1.5 w-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:240ms]" />
                    </span>
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="border-t border-slate-100 p-4 flex items-center gap-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Describe your trip…"
                data-testid="quote-input"
                className="flex-1 outline-none text-sm text-[#0B192C] placeholder:text-slate-400 px-3 py-3 border border-slate-200 focus:border-[#FF5238] transition-colors"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="quote-send-btn"
                className="btn-primary !py-3 !px-5 disabled:opacity-50"
              >
                <Send className="h-4 w-4" /> Send
              </button>
            </form>

            {/* Route-to-sales handoff */}
            {sessionId && !routed && (
              <div className="border-t border-slate-100 p-5 bg-[#FDFCF8]" data-testid="handoff-card">
                {!showHandoff ? (
                  <button
                    type="button"
                    onClick={() => setShowHandoff(true)}
                    data-testid="handoff-open-btn"
                    className="w-full flex items-center justify-between gap-4 text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-[#FF5238]" />
                      <div>
                        <div className="font-heading text-sm font-semibold text-[#0B192C]">
                          Route this query to our sales team
                        </div>
                        <div className="text-xs text-slate-500">
                          Your conversation will be sent to <span className="text-[#0B192C] font-medium">{SALES_EMAIL}</span>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-heading text-[#FF5238] group-hover:underline">
                      Route →
                    </span>
                  </button>
                ) : (
                  <form onSubmit={handleRoute} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      required
                      type="text"
                      placeholder="Full name *"
                      value={handoff.name}
                      onChange={(e) => setHandoff({ ...handoff, name: e.target.value })}
                      data-testid="handoff-name"
                      className="bg-white border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF5238]"
                    />
                    <input
                      required
                      type="email"
                      placeholder="Email *"
                      value={handoff.email}
                      onChange={(e) => setHandoff({ ...handoff, email: e.target.value })}
                      data-testid="handoff-email"
                      className="bg-white border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF5238]"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={handoff.phone}
                      onChange={(e) => setHandoff({ ...handoff, phone: e.target.value })}
                      data-testid="handoff-phone"
                      className="bg-white border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF5238]"
                    />
                    <input
                      type="text"
                      placeholder="Company (optional)"
                      value={handoff.company}
                      onChange={(e) => setHandoff({ ...handoff, company: e.target.value })}
                      data-testid="handoff-company"
                      className="bg-white border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#FF5238]"
                    />
                    <div className="md:col-span-2 flex items-center gap-3 pt-1">
                      <button
                        type="submit"
                        disabled={routing || !handoff.name || !handoff.email}
                        data-testid="handoff-submit"
                        className="btn-primary !py-3 !px-5 disabled:opacity-50"
                      >
                        {routing ? "Routing…" : `Send to ${SALES_EMAIL}`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowHandoff(false)}
                        className="text-xs text-slate-500 hover:text-[#FF5238]"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {routed && (
              <div className="border-t border-slate-100 p-5 bg-[#0B192C] text-white flex items-center gap-3" data-testid="handoff-success">
                <CheckCircle2 className="h-5 w-5 text-[#FF5238] shrink-0" />
                <div>
                  <div className="font-heading text-sm font-semibold">Query routed successfully</div>
                  <div className="text-xs text-slate-300 mt-0.5">
                    We sent your conversation to <span className="text-white font-medium">{SALES_EMAIL}</span>. A dedicated consultant will follow up within 24 hours.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
